import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { visitorService } from './services/visitorService';
import { ThemeProvider } from './contexts/ThemeContext';
import PrivateRoute from './components/PrivateRoute/PrivateRoute';
import Landing from './pages/Landing/Landing';
import Terms from './pages/Terms/Terms';
import Privacy from './pages/Privacy/Privacy';
import Account from './pages/Account/Account';
import Support from './pages/Support/Support';  // Add this import
import LoginModal from './components/Auth/LoginModal';
import RegisterModal from './components/Auth/RegisterModal';
import ForgotPasswordModal from './components/Auth/ForgotPasswordModal';
import ResetPasswordModal from './components/Auth/ResetPasswordModal';
import Wallet from './pages/Wallet/Wallet';  // Add this import
import DailyHoroscope from './pages/DailyHoroscope/DailyHoroscope';
import FreeTarot from './pages/FreeTarot/FreeTarot';
import YesNoTarot from './pages/YesNoTarot/YesNoTarot';
import ThreeCardTarot from './pages/ThreeCardTarot/ThreeCardTarot';
import ChatWidget from './components/ChatWidget/ChatWidget';
import AskAnAi from './pages/AskAnAi/AskAnAi';
import AiPsychic from './pages/AiPsychic/AiPsychic';
import HowItWorks from './pages/HowItWorks/HowItWorks';
import LanguageLanding from './pages/LanguageLanding/LanguageLanding';
import CardMeaningsHub from './pages/CardMeanings/CardMeaningsHub';
import CardMeaningPage from './pages/CardMeanings/CardMeaningPage';
import CardOfTheDay from './pages/CardMeanings/CardOfTheDay';
import LearnHub from './pages/LearnTarot/LearnHub';
import LearnArticle from './pages/LearnTarot/LearnArticle';
import { LEARN_LANG_CODES, LEARN_TAROT_ENABLED } from './pages/LearnTarot/learnData';
import LoveTarot from './pages/ReadingTypes/LoveTarot';
import CareerTarot from './pages/ReadingTypes/CareerTarot';
import CelticCrossTarot from './pages/ReadingTypes/CelticCrossTarot';
import DailyTarot from './pages/ReadingTypes/DailyTarot';
import CardOfTheDayReading from './pages/ReadingTypes/CardOfTheDayReading';
import NewLoveTarot from './pages/ReadingTypes/NewLoveTarot';
import MindBodySpiritTarot from './pages/ReadingTypes/MindBodySpiritTarot';
import SixCardTarot from './pages/ReadingTypes/SixCardTarot';
import BanIpConfirm from './pages/BanIpConfirm/BanIpConfirm';
import StreamBanConfirm from './pages/StreamBanConfirm/StreamBanConfirm';
import LlmChat from './pages/LlmChat/LlmChat';
import Translate from './pages/Translate/Translate';
import BirthChart from './pages/BirthChart/BirthChart';
import ChartLanding from './pages/BirthChart/ChartLanding';
import SharedChart from './pages/BirthChart/SharedChart';
import MoonPhase from './pages/MoonPhase/MoonPhase';
import LifePath from './pages/LifePath/LifePath';
import BirthCard from './pages/BirthCard/BirthCard';
import VenusSign from './pages/VenusSign/VenusSign';
import CompatibilityHub from './pages/Compatibility/CompatibilityHub';
import CompatibilityRoute from './pages/Compatibility/CompatibilityRoute';
import Synastry from './pages/Synastry/Synastry';
import Interpreter from './pages/Interpreter/Interpreter';
import SharedSynastry from './pages/Synastry/SharedSynastry';
import MoonBadge from './components/MoonBadge/MoonBadge';
import BanTokenEnforcer from './components/BanTokenEnforcer/BanTokenEnforcer';
import ProviderBanner from './components/ProviderBanner/ProviderBanner';
import { SITE_LANGUAGE_CODES } from './data/siteLanguages';
import './App.css';
import './styles/theme.css';

// One notice per page, including astrology tools and pages added later.
const VisitorTracker = () => {
  const { pathname } = useLocation();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (loading || navigator.userAgent === 'ReactSnap') return;
    visitorService.trackVisitor(pathname, user);
  }, [pathname, user, loading]);

  return null;
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Create a wrapper component to handle modal logic
const ModalManager = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  useEffect(() => {
    setShowLogin(location.pathname === '/login');
    setShowRegister(location.pathname === '/register');
    setShowForgotPassword(location.pathname === '/forgot-password');
    setShowResetPassword(location.pathname === '/reset-password');
  }, [location]);

  const handleCloseModal = () => {
    navigate('/');
  };

  const switchToLogin = () => {
    navigate('/login');
  };

  const switchToRegister = () => {
    navigate('/register');
  };

  const switchToForgotPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <>
      {showLogin && (
        <LoginModal 
          onClose={handleCloseModal}
          onSwitchToRegister={switchToRegister}
          onSwitchToForgotPassword={switchToForgotPassword}
        />
      )}
      {showRegister && (
        <RegisterModal 
          onClose={handleCloseModal}
          onSwitchToLogin={switchToLogin}
        />
      )}
      {showForgotPassword && (
        <ForgotPasswordModal
          onClose={handleCloseModal}
          onSwitchToLogin={switchToLogin}
        />
      )}
      {showResetPassword && (
        <ResetPasswordModal
          token={new URLSearchParams(window.location.search).get('token')}
          onClose={() => navigate('/login')}
        />
      )}
    </>
  );
};

function LearnTarotFirst() {
  const { first } = useParams();
  if (first === 'en') return <Navigate to="/learn-tarot/" replace />;
  if (LEARN_LANG_CODES.includes(first)) return <LearnHub lang={first} />;
  return <LearnArticle lang="en" slug={first} />;
}

function LearnTarotLocalized() {
  const { lang, slug } = useParams();
  if (lang === 'en') return <Navigate to={`/learn-tarot/${slug}/`} replace />;
  if (!LEARN_LANG_CODES.includes(lang)) return <Navigate to="/learn-tarot/" replace />;
  return <LearnArticle lang={lang} slug={slug} />;
}

function App() {
  return (
    <HelmetProvider>
    <ThemeProvider>
    <AuthProvider>
      <Router>
        <div className="App">
          <ProviderBanner />
          <VisitorTracker />
          <ScrollToTop />
          <BanTokenEnforcer />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/birth-chart/" element={<BirthChart />} />
            <Route path="/personalized-daily-horoscope/" element={<DailyHoroscope />} />
            <Route path="/birth-chart/shared/:id/" element={<SharedChart />} />
            <Route path="/big-three-calculator/" element={<ChartLanding key="big-three" variant="big-three" />} />
            <Route path="/rising-sign-calculator/" element={<ChartLanding key="rising" variant="rising" />} />
            <Route path="/moon-sign-calculator/" element={<ChartLanding key="moon" variant="moon" />} />
            <Route path="/moon-phase-today/" element={<MoonPhase />} />
            <Route path="/life-path-number-calculator/" element={<LifePath />} />
            <Route path="/tarot-birth-card-calculator/" element={<BirthCard />} />
            <Route path="/venus-sign-calculator/" element={<VenusSign />} />
            <Route path="/zodiac-compatibility/" element={<CompatibilityHub />} />
            <Route path="/zodiac-compatibility/:slug/" element={<CompatibilityRoute />} />
            <Route path="/birth-chart-compatibility/" element={<Synastry />} />
            <Route path="/birth-chart-compatibility/shared/:id/" element={<SharedSynastry />} />
            <Route path="/tarot-card-interpreter/" element={<Interpreter />} />
            <Route path="/free-tarot/" element={<FreeTarot />} />
            <Route path="/yes-or-no-tarot/" element={<YesNoTarot />} />
            <Route path="/three-card-tarot/" element={<ThreeCardTarot />} />
            <Route path="/ask-an-ai/" element={<AskAnAi />} />
            <Route path="/ai-psychic/" element={<AiPsychic />} />
            <Route path="/ai-psychic/:lang/" element={<AiPsychic />} />
            <Route path="/chat-room/" element={<Navigate to="/" replace />} />
            <Route path="/how-it-works/" element={<HowItWorks />} />
            {LEARN_TAROT_ENABLED && <Route path="/learn-tarot/" element={<LearnHub lang="en" />} />}
            {LEARN_TAROT_ENABLED && <Route path="/learn-tarot/:first/" element={<LearnTarotFirst />} />}
            {LEARN_TAROT_ENABLED && <Route path="/learn-tarot/:lang/:slug/" element={<LearnTarotLocalized />} />}
            <Route path="/love-tarot-reading/" element={<LoveTarot />} />
            <Route path="/career-tarot-reading/" element={<CareerTarot />} />
            <Route path="/celtic-cross-tarot/" element={<CelticCrossTarot />} />
            <Route path="/daily-tarot-reading/" element={<DailyTarot />} />
            <Route path="/new-love-tarot-reading/" element={<NewLoveTarot />} />
            <Route path="/mind-body-spirit-tarot/" element={<MindBodySpiritTarot />} />
            <Route path="/six-card-tarot-spread/" element={<SixCardTarot />} />
            <Route path="/tarot-card-meanings/" element={<CardMeaningsHub lang="en" />} />
            <Route path="/tarot-card-meanings/:slug/" element={<CardMeaningPage lang="en" />} />
            <Route path="/es/significados-cartas-tarot/" element={<CardMeaningsHub lang="es" />} />
            <Route path="/es/significados-cartas-tarot/:slug/" element={<CardMeaningPage lang="es" />} />
            <Route path="/ar/tarot-card-meanings/" element={<CardMeaningsHub lang="ar" />} />
            <Route path="/ar/tarot-card-meanings/:slug/" element={<CardMeaningPage lang="ar" />} />
            <Route path="/zh/taluo-pai-yisi/" element={<CardMeaningsHub lang="zh" />} />
            <Route path="/zh/taluo-pai-yisi/:slug/" element={<CardMeaningPage lang="zh" />} />
            <Route path="/de/tarot-kartenbedeutungen/" element={<CardMeaningsHub lang="de" />} />
            <Route path="/de/tarot-kartenbedeutungen/:slug/" element={<CardMeaningPage lang="de" />} />
            <Route path="/pl/znaczenia-kart-tarota/" element={<CardMeaningsHub lang="pl" />} />
            <Route path="/pl/znaczenia-kart-tarota/:slug/" element={<CardMeaningPage lang="pl" />} />
            <Route path="/pt/significados-cartas-tarot/" element={<CardMeaningsHub lang="pt" />} />
            <Route path="/pt/significados-cartas-tarot/:slug/" element={<CardMeaningPage lang="pt" />} />
            <Route path="/fr/significations-cartes-tarot/" element={<CardMeaningsHub lang="fr" />} />
            <Route path="/fr/significations-cartes-tarot/:slug/" element={<CardMeaningPage lang="fr" />} />
            <Route path="/card-of-the-day/" element={<CardOfTheDayReading />} />
            <Route path="/es/carta-del-dia/" element={<CardOfTheDay lang="es" />} />
            <Route path="/ar/card-of-the-day/" element={<CardOfTheDay lang="ar" />} />
            <Route path="/ban-ip/confirm/" element={<BanIpConfirm />} />
            <Route path="/stream-ban/confirm" element={<StreamBanConfirm />} />
            <Route path="/stream-ban/confirm/" element={<StreamBanConfirm />} />
            {SITE_LANGUAGE_CODES.map((language) => (
              <Route key={language} path={`/${language}/`} element={<LanguageLanding language={language} />} />
            ))}
            <Route path="/terms/" element={<Terms />} />
            <Route path="/privacy/" element={<Privacy />} />
            <Route path="/login" element={<Landing />} />
            <Route path="/register" element={<Landing />} />
            <Route path="/forgot-password" element={<Landing />} />
            <Route path="/reset-password" element={<Landing />} />
            {/* Update to Home route */}
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route
              path="/account"
              element={
                <PrivateRoute>
                  <Account />
                </PrivateRoute>
              }
            />
            <Route
              path="/support"
              element={
                <PrivateRoute>
                  <Support />
                </PrivateRoute>
              }
            />
            <Route
              path="/wallet"
              element={
                <PrivateRoute>
                  <Wallet />
                </PrivateRoute>
              }
            />
            <Route
              path="/llm"
              element={
                <PrivateRoute>
                  <LlmChat />
                </PrivateRoute>
              }
            />
            <Route
              path="/translate"
              element={
                <PrivateRoute>
                  <Translate />
                </PrivateRoute>
              }
            />
          </Routes>
          <ModalManager />
          <MoonBadge />
          <ChatWidget />
        </div>
      </Router>
    </AuthProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
